/**
 * The vendor's announcements for this tenant (phase 7b item 5), above every
 * page: one strip per announcement in force, styled by severity — critical
 * and major as destructive, minor as warning, info as standby — each
 * dismissible (the container remembers the dismissal per version, so an
 * update to an incident shows again).
 */
import type { TenantAnnouncement } from '@acp/api-contract/vendor.js';
import { InfoIcon, TriangleAlertIcon, WrenchIcon, XIcon } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Button } from './ui/button.js';

export type AnnouncementBannerProps = {
  readonly announcements: readonly TenantAnnouncement[];
  readonly onDismiss: (announcement: TenantAnnouncement) => void;
};

const TONE: Readonly<Record<TenantAnnouncement['severity'], string>> = {
  critical: 'border-destructive bg-destructive text-destructive-foreground',
  major: 'border-destructive bg-destructive text-destructive-foreground',
  minor: 'border-warning bg-warning text-warning-foreground',
  info: 'border-standby bg-standby text-standby-foreground',
};

export function AnnouncementBanner({ announcements, onDismiss }: AnnouncementBannerProps) {
  if (announcements.length === 0) return null;
  return (
    <div className="grid gap-1 px-4 pt-2" data-testid="announcement-banner">
      {announcements.map((a) => {
        const Icon = a.kind === 'maintenance' ? WrenchIcon : a.kind === 'incident' ? TriangleAlertIcon : InfoIcon;
        return (
          <div key={a.announcementId} role={a.severity === 'critical' || a.severity === 'major' ? 'alert' : 'status'} className={cn('flex items-start gap-3 rounded-md border px-4 py-2 text-sm', TONE[a.severity])} data-testid="announcement" data-severity={a.severity}>
            <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <strong>{a.title}</strong>
              {a.body === '' ? null : <span className="ml-2">{a.body}</span>}
              {a.endsAt === null ? null : <span className="ml-2 opacity-80">(until {new Date(a.endsAt).toLocaleString()})</span>}
            </div>
            <Button size="icon" variant="ghost" className="size-6 shrink-0" aria-label={`Dismiss “${a.title}”`} onClick={() => onDismiss(a)} data-testid="dismiss-announcement">
              <XIcon />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
